import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Profile {
  profileId: string;
  profileType: "internal" | "external";
  email: string;
  displayName: string | null;
  rolePreference?: string | null;
  isDefault: boolean;
}

interface ProfileContextValue {
  /** All profiles for the authenticated user. */
  profiles: Profile[];
  /** The currently active profile (resolved via priority chain). */
  activeProfile: Profile | null;
  /** Convenience accessor for the active profile type. */
  activeProfileType: "internal" | "external" | null;
  /** Switch the active profile for this session. Persists to localStorage. */
  switchProfile: (profileType: "internal" | "external") => void;
  /** Set a profile as the user's cross-device default (calls backend PUT). */
  setDefaultProfile: (profileType: "internal" | "external") => Promise<void>;
  /** Whether the user has more than one profile (controls switcher visibility). */
  hasMultipleProfiles: boolean;
  /** True while profiles are being fetched from the backend. */
  loading: boolean;
  /** Error message if profile fetch failed. */
  error: string | null;
  /** Re-fetch profiles from the backend. */
  refetch: () => Promise<void>;
}

interface ProfileProviderProps {
  apiRequest: (path: string, init?: RequestInit) => Promise<Response>;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOCAL_STORAGE_KEY = "binaism:active_profile_type";
const VALID_TYPES = new Set<string>(["internal", "external"]);

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ProfileContext = createContext<ProfileContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read and validate the active profile type from localStorage. */
function readLocalStorage(): "internal" | "external" | null {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored && VALID_TYPES.has(stored)) {
      return stored as "internal" | "external";
    }
  } catch {
    // localStorage may be unavailable (e.g., incognito, SSR)
  }
  return null;
}

/** Persist the active profile type to localStorage. */
function writeLocalStorage(profileType: "internal" | "external"): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, profileType);
  } catch {
    // Silently ignore — session will still work, just won't persist
  }
}

/**
 * Resolve which profile should be active using the priority chain:
 * 1. localStorage value (if it matches a real profile)
 * 2. Backend is_default flag
 * 3. Hard fallback: "external"
 */
function resolveActiveProfile(profiles: Profile[]): Profile | null {
  if (profiles.length === 0) return null;
  if (profiles.length === 1) return profiles[0] ?? null;

  // Priority 1: localStorage
  const storedType = readLocalStorage();
  if (storedType) {
    const match = profiles.find((p) => p.profileType === storedType);
    if (match) return match;
  }

  // Priority 2: backend is_default
  const defaultProfile = profiles.find((p) => p.isDefault);
  if (defaultProfile) return defaultProfile;

  // Priority 3: hard fallback to external
  const external = profiles.find((p) => p.profileType === "external");
  if (external) return external;

  // Absolute fallback: first profile
  return profiles[0] ?? null;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ProfileProvider({
  apiRequest,
  children,
}: ProfileProviderProps): React.ReactElement {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest("/api/profiles");

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        setError(data.error ?? "Unable to load profiles");
        setLoading(false);
        return;
      }

      const data = (await response.json()) as { profiles?: Profile[] };
      const fetched = data.profiles ?? [];

      setProfiles(fetched);

      const resolved = resolveActiveProfile(fetched);
      setActiveProfile(resolved);

      // Sync localStorage with the resolved value
      if (resolved) {
        writeLocalStorage(resolved.profileType);
      }

      setLoading(false);
    } catch {
      setError("Network error. Unable to load profiles.");
      setLoading(false);
    }
  }, [apiRequest]);

  // Fetch profiles on mount
  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  const switchProfile = useCallback(
    (profileType: "internal" | "external"): void => {
      const target = profiles.find((p) => p.profileType === profileType);
      if (target) {
        setActiveProfile(target);
        writeLocalStorage(profileType);
      }
    },
    [profiles]
  );

  const setDefaultProfile = useCallback(
    async (profileType: "internal" | "external"): Promise<void> => {
      try {
        const response = await apiRequest("/api/profiles/default", {
          method: "PUT",
          body: JSON.stringify({ profileType }),
        });

        if (!response.ok) {
          const data = (await response.json().catch(() => ({}))) as {
            error?: string;
          };
          throw new Error(data.error ?? "Unable to update default profile");
        }

        // Update local state to reflect the new default
        setProfiles((prev) =>
          prev.map((p) => ({
            ...p,
            isDefault: p.profileType === profileType,
          }))
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to update default profile";
        throw new Error(message);
      }
    },
    [apiRequest]
  );

  const hasMultipleProfiles = profiles.length > 1;

  const value = useMemo<ProfileContextValue>(
    () => ({
      profiles,
      activeProfile,
      activeProfileType: activeProfile?.profileType ?? null,
      switchProfile,
      setDefaultProfile,
      hasMultipleProfiles,
      loading,
      error,
      refetch: fetchProfiles,
    }),
    [
      profiles,
      activeProfile,
      switchProfile,
      setDefaultProfile,
      hasMultipleProfiles,
      loading,
      error,
      fetchProfiles,
    ]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useProfiles(): ProfileContextValue {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfiles must be used within a ProfileProvider");
  }
  return context;
}
